import { BoxGeometry, BufferAttribute, BufferGeometry, Float32BufferAttribute, OctahedronGeometry, PlaneGeometry, TetrahedronGeometry, } from 'three/webgpu';
/**
 * Geometry sources for the mesh + procMesh renderers (plan §3.1/§3.3).
 * Everything is unit-sized; per-particle size/curves scale it in the shader.
 */
const cache = new Map();
function cached(key, make) {
    let g = cache.get(key);
    if (!g) {
        g = make();
        cache.set(key, g);
    }
    return g;
}
/** Low-poly debris shard: a tetrahedron with deterministic vertex jitter. */
function shardGeometry() {
    const geo = new TetrahedronGeometry(0.7).toNonIndexed();
    const pos = geo.attributes.position;
    for (let i = 0; i < pos.count; i++) {
        // deterministic per-vertex jitter (no Math.random — determinism convention)
        const j = Math.sin(i * 12.9898) * 0.18;
        pos.setXYZ(i, pos.getX(i) * (1 + j), pos.getY(i) * (1 - j * 0.5), pos.getZ(i) * (1 + j * 0.7));
    }
    geo.computeVertexNormals();
    return geo;
}
export function builtinMeshGeometry(id) {
    switch (id) {
        case 'cube':
            return cached('cube', () => new BoxGeometry(0.8, 0.8, 0.8));
        case 'shard':
            return cached('shard', shardGeometry);
        case 'star':
            return cached('star', () => new OctahedronGeometry(0.7).scale(1, 1.6, 1));
        case 'quad':
            return cached('quad', () => new PlaneGeometry(1, 1));
    }
}
/**
 * Annulus strip for the shockwave ring. The shader expands it radially:
 * vertex position.xz is the unit direction, uv.y ∈ {0 inner, 1 outer} selects
 * the rim, uv.x runs around the ring. Radius/width arrive via curves.
 */
function ringGeometry(segments) {
    const positions = [];
    const uvs = [];
    const indices = [];
    for (let i = 0; i <= segments; i++) {
        const a = (i / segments) * Math.PI * 2;
        const x = Math.cos(a);
        const z = Math.sin(a);
        positions.push(x, 0, z, x, 0, z); // inner, outer share the direction
        uvs.push(i / segments, 0, i / segments, 1);
    }
    for (let i = 0; i < segments; i++) {
        const b = i * 2;
        indices.push(b, b + 1, b + 2, b + 1, b + 3, b + 2);
    }
    const geo = new BufferGeometry();
    geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geo.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
    geo.setIndex(indices);
    return geo;
}
/** N-spike flat star/flare in the XZ plane (radial burst mesh). */
function burstGeometry(spikes, innerRatio) {
    const positions = [0, 0, 0];
    const uvs = [0.5, 0.5];
    const indices = [];
    const n = spikes * 2;
    for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        const r = i % 2 === 0 ? 1 : innerRatio;
        const x = Math.cos(a) * r;
        const z = Math.sin(a) * r;
        positions.push(x, 0, z);
        uvs.push(x * 0.5 + 0.5, z * 0.5 + 0.5);
        indices.push(0, ((i + 1) % n) + 1, i + 1);
    }
    const geo = new BufferGeometry();
    geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geo.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
    geo.setIndex(indices);
    return geo;
}
/**
 * Curved ribbon arc in the XZ plane for slashes. uv.x runs along the arc
 * (scroll/dissolve direction), uv.y across the blade. Width tapers to points
 * at both ends with a sine profile — the classic slash silhouette.
 */
function slashArcGeometry(arcDegrees, widthRatio, segments) {
    const positions = [];
    const uvs = [];
    const indices = [];
    const arc = (arcDegrees * Math.PI) / 180;
    for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const a = (t - 0.5) * arc;
        const w = Math.sin(t * Math.PI) * widthRatio;
        const cx = Math.cos(a);
        const cz = Math.sin(a);
        positions.push(cx * (1 - w / 2), 0, cz * (1 - w / 2), cx * (1 + w / 2), 0, cz * (1 + w / 2));
        uvs.push(t, 0, t, 1);
    }
    for (let i = 0; i < segments; i++) {
        const b = i * 2;
        indices.push(b, b + 1, b + 2, b + 1, b + 3, b + 2);
    }
    const geo = new BufferGeometry();
    geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geo.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
    geo.setIndex(indices);
    return geo;
}
/**
 * Beam parameter strip: `position` encodes (t along beam, side −1/+1, 0) —
 * the REAL positions are computed in the vertex stage (Bézier sweep +
 * camera-facing expansion, see buildBeamMaterial). uv = (t, side01) so the
 * whole texture/dissolve/scroll pipeline works along the beam unchanged.
 */
function beamStripGeometry(segments) {
    const positions = [];
    const uvs = [];
    const indices = [];
    for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        positions.push(t, -1, 0, t, 1, 0);
        uvs.push(t, 0, t, 1);
    }
    for (let i = 0; i < segments; i++) {
        const b = i * 2;
        indices.push(b, b + 1, b + 2, b + 1, b + 3, b + 2);
    }
    const geo = new BufferGeometry();
    geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geo.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
    geo.setIndex(indices);
    return geo;
}
export function beamGeometry(segments = 24) {
    return cached(`beam_${segments}`, () => beamStripGeometry(segments));
}
export function procMeshGeometry(shape) {
    switch (shape.kind) {
        case 'ring':
            return cached(`ring_${shape.segments ?? 64}`, () => ringGeometry(shape.segments ?? 64));
        case 'burst':
            return cached(`burst_${shape.spikes}_${shape.innerRatio ?? 0.35}`, () => burstGeometry(Math.max(2, shape.spikes), shape.innerRatio ?? 0.35));
        case 'slashArc':
            return cached(`slash_${shape.arcDegrees}_${shape.widthRatio ?? 0.35}_${shape.segments ?? 48}`, () => slashArcGeometry(shape.arcDegrees, shape.widthRatio ?? 0.35, shape.segments ?? 48));
        case 'quad':
            return cached(`pquad_${shape.orientation}`, () => {
                const geo = new PlaneGeometry(1, 1);
                if (shape.orientation === 'ground')
                    geo.rotateX(-Math.PI / 2);
                return geo;
            });
    }
}
